/* rr-compact.js: compact sticky header (brand spec v2.16 6D, references
   sticky-header-compact.html desktop option A and mobile option 1, and
   vehicle-bar-morph.html for the phone fold; 28 Sept 2026).

   - <html> gets .rr-compact once the page scrolls past the threshold and
     loses it 30 to 40px higher; the gap stops it flickering. Phones: 100px
     on, 70px off. Desktop (v2.18): the vehicle bar is sticky under the full
     header, and the thresholds come from the real stack (deskStack): off
     where the compact row covers everything the full stack held, on 40px
     further down. One passive scroll listener, one rAF.
   - Desktop: the header section keeps its rest height in the page flow
     (min-height, set at rest) while its content folds to one 60px row, so
     nothing below moves and Chrome's scroll anchoring has nothing to undo.
     The transparent remainder passes clicks through (rr-brand.css 6e).
     A "Menu" button (button.rr-hmenu) goes before the logo; in compact it
     drops the folded nav row back down (html.rr-navopen).
   - Phones: a search icon (button.rr-msico, data-rr-msearch-open, which
     rr-search.js already treats as the sheet opener) goes after the menu
     drawer, so the burger's "+ .mobile-menu" opener keeps working; it shows
     in compact, when the search pill folds away.
   - --rr-announcement-h and --rr-mstack-h (announcement plus phone header)
     follow the real heights through a ResizeObserver, so the stuck header
     and the sticky phone vehicle bar move with the slimming bar.
   - window.rrCompact: { on(), measure(on, fn) } and the document events
     rr:compact-before / rr:compact ({ detail: { on } }). measure() applies
     the target state with transitions off, runs fn (for FLIP rects), then
     restores, so rr-finder.js can measure the car icon's final slot before
     the class changes for real.
   Reduced motion: the switch is instant (rr-brand.css turns the
   transitions off).

   6n (29 Sept 2026, owner: "still choppy"): nothing in the layout animates
   any more (rr-brand.css 6n). Desktop: the full header is in the page flow
   and scrolls away under the announcement bar, the vehicle bar sticks under
   the announcement bar, and compact starts only once the full header is out
   of view (rest height + 160px, off at + 120px): the compact row is laid out
   off screen and slides down (translateY, Web Animations); on the way up it
   slides back up and only then leaves compact, so the full header returns in
   the flow where it belongs. Phones: CSS transitions on transform and opacity
   only. --rr-ann-slim, --rr-mhead-k and --rr-mrow-shift are written only
   when a size really changes, never during a fold. */
(function () {
  'use strict';
  try {
    var doc = document;
    var win = window;
    var html = doc.documentElement;
    if (!html || !doc.addEventListener) return;

    var DESK_MQ = '(min-width: 799px)';
    var ON_DESK = 120;
    var ON_PHONE = 100;
    /* leaving compact on the way up (owner, 28 Sept: "too late, decent white
       space before anything shows up"): the header's rest space is held in
       the page, so between these and the top it was empty under the compact
       row. The full header now comes back well before the top (hysteresis
       kept: 30px under the on thresholds). */
    var OFF_DESK = 90;
    var OFF_PHONE = 70;
    var OFF = 20;   /* legacy name, the desktop glide-to-top below uses OFF_DESK */
    var compact = false;
    var queued = false;
    var restH = 0;
    /* v2.18 6D desktop fold: the thresholds come from the real stack. The
       vehicle bar is sticky under the full header (rr-brand.css 6m), so it
       never scrolls away before the fold. Compact starts only once the page
       is scrolled far enough that the compact row covers everything the
       full stack held (OFF), plus 40px of hysteresis (ON), so there is never
       a white gap under the compact header in either direction. */
    var HYST = 40;
    var dOn = ON_DESK;
    var dOff = OFF_DESK;
    /* 6n desktop slide */
    /* v7.1 motion review F9: both directions on the 300ms fold token and the
       brand curve, so the compact row and the slimming announcement bar
       (--rr-fold-ms) land together */
    var SLIDE_IN_MS = 300;
    var SLIDE_OUT_MS = 300;
    var hdrAnim = null;
    var rowH = 0;
    var searchB = 0;   /* the search field's bottom, from the section top, at rest */
    var ghostEl = null;
    var ghostTimer = null;
    var lastScroll = 0;
    var exiting = false;
    var vars = {};

    function isDesk() {
      try { return win.matchMedia(DESK_MQ).matches; } catch (e) { return true; }
    }
    function q(sel, root) { try { return (root || doc).querySelector(sel); } catch (e) { return null; } }
    function raf(fn) { (win.requestAnimationFrame || function (f) { return win.setTimeout(f, 16); })(fn); }
    function fire(name, on) {
      var ev;
      try { ev = new CustomEvent(name, { detail: { on: on } }); }
      catch (e) { ev = doc.createEvent('CustomEvent'); ev.initCustomEvent(name, false, false, { on: on }); }
      doc.dispatchEvent(ev);
    }

    function section() { return q('.header-section'); }

    /* desktop: hold the section's rest height in the flow */
    function holdRest() {
      var s = section();
      if (!s) return;
      if (!isDesk()) { s.style.minHeight = ''; return; }
      if (compact) return;
      s.style.minHeight = '';
      restH = s.offsetHeight;
      if (restH) s.style.minHeight = restH + 'px';
      var row = q('#header .header__inner-content');
      rowH = row ? Math.round(row.offsetHeight) : 0;
      var form = q('#header .header__search form') || q('#header form');
      var st = s.getBoundingClientRect().top;
      searchB = form ? Math.round(form.getBoundingClientRect().bottom - st) : 0;
      deskStack();
    }

    /* 6n stand-in: an inert copy of the full header, laid out in the section
       and shown (visibility only) while the real one is the compact row.
       Built in a quiet moment, and again after each return to the full
       header so it carries the current cart count. */
    function buildGhost() {
      var s = section();
      var h = hdrEl();
      if (!s || !h || compact || !isDesk()) return;
      var g = h.cloneNode(true);
      g.removeAttribute('id');
      g.classList.add('rr-hghost');
      g.setAttribute('aria-hidden', 'true');
      /* not inert (4 Oct 2026, owner: the menu links that still show under
         the compact row could not be clicked): its menu links take clicks
         (rr-brand.css 6n), everything stays out of the tab order below and
         the rest of the copy is click-through */
      /* the menu dropdowns stay (4 Oct 2026, owner): Categories and Featured
         Vehicles open in the copy too (rr-motion.js rrMenuInit below) */
      var drop = g.querySelectorAll('.rr-hmenu, .rr-vchip, script, #cloud-search-results, .rr-search__panel, .header__search .rr-search__sugg');
      for (var i = 0; i < drop.length; i++) if (drop[i].parentNode) drop[i].parentNode.removeChild(drop[i]);
      var all = g.querySelectorAll('[id], [name], [tabindex], a, button, input, select, textarea');
      for (var j = 0; j < all.length; j++) {
        all[j].removeAttribute('id');
        all[j].removeAttribute('name');
        all[j].setAttribute('tabindex', '-1');
      }
      /* after the real header, so querySelector still finds the real form first */
      if (ghostEl && ghostEl.parentNode) ghostEl.parentNode.replaceChild(g, ghostEl);
      else h.parentNode.insertBefore(g, h.nextSibling);
      ghostEl = g;
      if (win.rrMenuInit) win.rrMenuInit(g);
      watchGhostNav();
    }
    /* 4 Oct 2026 (owner): the compact row's Menu button waits until the
       stand-in's menu row has scrolled under the compact row, the way the
       vehicle chip waits for the vehicle bar, so the two menus never show
       together. html.rr-gnav-in while any of that row shows below the compact
       row (an IntersectionObserver, no scroll work). The compact row's bottom
       comes from the inline announcement variables plus the row height read
       once after a landing. */
    var navIO = null;
    var navTop = -1;
    var cRowH = 60;
    function navRow() {
      var item = ghostEl && q('.navbar-item.header__item', ghostEl);
      return item ? item.parentNode : null;
    }
    function watchGhostNav(top) {
      var row = navRow();
      if (!row || !('IntersectionObserver' in win) || !isDesk()) return;
      if (typeof top !== 'number') {
        var a = parseFloat(html.style.getPropertyValue('--rr-announcement-h')) || 0;
        var sl = parseFloat(html.style.getPropertyValue('--rr-ann-slim')) || 0;
        top = Math.round(a - sl + cRowH);
      }
      if (navIO && top === navTop && navIO.rrRow === row) return;
      if (navIO) navIO.disconnect();
      navTop = top;
      navIO = new IntersectionObserver(function (entries) {
        var en = entries[entries.length - 1];
        html.classList.toggle('rr-gnav-in', en.isIntersecting);
      }, { rootMargin: (-top) + 'px 0px 0px 0px', threshold: 0 });
      navIO.rrRow = row;
      navIO.observe(row);
    }

    function queueGhost() {
      win.clearTimeout(ghostTimer);
      ghostTimer = win.setTimeout(function run() {
        if (compact || exiting) return;
        if (Date.now() - lastScroll < 250) { ghostTimer = win.setTimeout(run, 300); return; }
        if (win.requestIdleCallback) win.requestIdleCallback(function () { if (!compact && !exiting) buildGhost(); }, { timeout: 2000 });
        else buildGhost();
      }, 500);
    }

    /* desktop: the vehicle bar sticks under the announcement bar (6n: the
       full header scrolls away in the flow), and the fold thresholds. The
       search row (rowH) passes under the announcement bar at scrollY =
       rowH: compact starts there, the stand-in carrying the rest of the full
       header on. On the way up compact ends 40px sooner, while the stand-in's
       search row is still hidden under the 60px compact row. */
    function deskStack() {
      var bar = annBar();
      var a = bar && annSticks() ? Math.round(bar.offsetHeight) : 0;
      setVar(html, '--rr-dbar-top', a + 'px');
      /* 29 Sept audit: the search field left the screen at scrollY about 92
         and compact came at 120, so for about 90px there was no search bar.
         Compact now comes 30px before the field reaches the announcement
         bar (the compact row slides in over it) and leaves 22px before that,
         while the stand-in's field is still under the 60px compact row. The
         stand-in's own row shows only while the compact row moves
         (html.rr-cmove), so no tail of it peeks out under the compact row. */
      var fb = searchB || rowH || Math.max(0, restH - 55);
      dOn = Math.max(40, Math.round(fb - 30));
      dOff = Math.max(20, dOn - 22);
    }

    function setVar(el, name, val) {
      var key = (el === html ? '' : 'mh') + name;
      if (vars[key] === val) return;
      vars[key] = val;
      el.style.setProperty(name, val);
    }

    /* phones (v2.17 fix, owner video 28 Sept "very choppy"): the theme's
       sticky wrapper followed the header down from its rest height to 52px
       and the vehicle bar collapsed, so everything below reflowed by about
       106px while the page was scrolling. The wrapper now keeps its rest
       height while compact (--rr-mhead-rest, rr-brand.css 6d.2) and the bar
       keeps its slot (rr-finder.js), so nothing below moves. */
    function holdPhoneRest() {
      /* read only at the very top, where the header is in the flow at its
         rest height (scrolled, the theme's fixed header grows 10px) */
      var y = win.pageYOffset || html.scrollTop || 0;
      if (isDesk() || compact || y > 4) return;
      var w = q('#mobile-header-sticky-wrapper');
      var h = w ? Math.round(w.offsetHeight) : 0;
      if (h > 0) setVar(html, '--rr-mhead-rest', h + 'px');   /* write on change only (F10) */
    }

    function setState(on) {
      html.classList.toggle('rr-compact', on);
      var s = section();
      if (s) s.classList.toggle('rr-stuck', on);   /* the 25 Sept compact logo rules and xcloud-instant-search.css key on it */
    }

    function measure(on, fn) {
      var was = html.classList.contains('rr-compact');
      html.classList.add('rr-nt');
      setState(on);
      void html.offsetHeight;
      var out;
      try { out = fn(); } catch (e) { out = null; }
      setState(was);
      void html.offsetHeight;
      html.classList.remove('rr-nt');
      void html.offsetHeight;
      return out;
    }

    /* 6n desktop: the compact row slides down from under the announcement
       bar on the way in, and back up on the way out */
    function hdrEl() { return q('.header-section #header'); }
    function slideIn(from) {
      var h = hdrEl();
      if (!h || !h.animate || reduced()) return;
      if (hdrAnim) { try { hdrAnim.cancel(); } catch (e) { /* gone */ } }
      html.classList.add('rr-cmove');
      var a = h.animate([
        { transform: from || 'translateY(-100%)' },
        { transform: 'none' }
      ], { duration: SLIDE_IN_MS, easing: 'cubic-bezier(.2,.8,.2,1)' });
      hdrAnim = a;
      a.onfinish = a.oncancel = function () {
        if (hdrAnim === a) hdrAnim = null;
        if (!exiting) html.classList.remove('rr-cmove');
        settled();
      };
    }
    /* XCloud pins its panel to the search form's rect, and it re-measures
       when the header's class changes, which is mid-slide: measure again
       once the row has landed */
    function settled() {
      /* next frame: the finish frame only swaps state (29 Sept audit: a
         50 to 117ms frame at the end of the slide) */
      raf(function () {
        try { if (win.rrSearch && win.rrSearch.xcloudSync) win.rrSearch.xcloudSync(); } catch (e) { /* optional */ }
        /* the compact row's real bottom, once per landing (one read, and the
           observer is only rebuilt when it moved) */
        if (compact && isDesk() && ghostEl) {
          var h = hdrEl();
          if (h) {
            var r = h.getBoundingClientRect();
            cRowH = Math.round(r.height) || cRowH;
            watchGhostNav(Math.round(r.bottom));
          }
        }
        fire('rr:compact-settled', compact);
      });
    }
    function endExit() {
      var a = hdrAnim;
      exiting = false;
      hdrAnim = null;
      if (!compact) setState(false);
      if (a) { try { a.cancel(); } catch (e) { /* gone */ } }
      html.classList.remove('rr-cmove');
      settled();
      queueGhost();
    }
    function slideOut() {
      var h = hdrEl();
      if (!h || !h.animate || reduced()) { endExit(); return; }
      if (hdrAnim) { try { hdrAnim.cancel(); } catch (e) { /* gone */ } }
      exiting = true;
      html.classList.add('rr-cmove');
      var a = h.animate([
        { transform: 'none' },
        { transform: 'translateY(-100%)' }
      ], { duration: SLIDE_OUT_MS, easing: 'cubic-bezier(.2,.8,.2,1)', fill: 'forwards' });
      hdrAnim = a;
      a.onfinish = function () { if (hdrAnim === a) endExit(); };
    }
    function reduced() {
      try { return win.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) { return false; }
    }

    function set(on) {
      if (on === compact) return;
      var desk = isDesk();
      /* holdRest (a forced layout) runs at boot, load and resize only */
      if (on && !exiting) { holdAnnRest(); holdPhoneRest(); }
      fire('rr:compact-before', on);
      compact = on;
      if (on) {
        /* back down during a slide out: carry on from where it is */
        var from = null;
        if (exiting && hdrAnim) {
          try { from = win.getComputedStyle(hdrEl()).transform; } catch (e) { from = null; }
          try { hdrAnim.cancel(); } catch (e) { /* gone */ }
          hdrAnim = null;
          exiting = false;
        }
        if (desk && !ghostEl) buildGhost();   /* a switch before the quiet moment came */
        setState(true);
        if (desk) slideIn(from && from !== 'none' ? from : null);
      } else {
        html.classList.remove('rr-navopen');
        var mb = q('.rr-hmenu');
        if (mb) mb.setAttribute('aria-expanded', 'false');
        if (desk) slideOut(); else setState(false);
      }
      fire('rr:compact', on);
    }

    function update() {
      queued = false;
      var y = win.pageYOffset || html.scrollTop || 0;
      var desk = isDesk();
      var th = desk ? dOn : ON_PHONE;
      if (y <= 4) holdPhoneRest();
      html.classList.toggle('rr-scrolled', y > 4);   /* the sticky phone vehicle bar gets its white backing */
      if (!compact && y > th) set(true);
      else if (compact && y < (desk ? dOff : OFF_PHONE)) set(false);
    }
    function onScroll() {
      if (queued) return;
      queued = true;
      raf(update);
    }

    /* ---- heights ---------------------------------------------------------- */
    function annBar() { return q('.announcement-bar'); }
    function annSticks() {
      var bar = annBar();
      if (!bar || bar.offsetHeight === 0) return false;
      var s = bar.closest ? bar.closest('.shopify-section') : null;
      var data = s ? q('script[data-section-data]', s) : null;
      if (data) {
        try { return JSON.parse(data.textContent || '{}').enable_sticky !== false; } catch (e) { return true; }
      }
      return true;
    }
    /* The theme's jQuery sticky plugin re-reads the announcement bar's height
       into its spacer (#sticky-wrapper) and caught it mid-slim (42px instead
       of 46px), which lifted the whole page 4px under the header when it
       came back (28 Sept). The spacer is pinned to the bar's rest height,
       read only while the header is not compact (rr-brand.css 6e). */
    function holdAnnRest() {
      var bar = annBar();
      if (!bar || compact) return;
      var h = Math.round(bar.offsetHeight);
      if (h > 0) setVar(html, '--rr-ann-rest', h + 'px');   /* write on change only (F10) */
    }

    function measureVars() {
      var bar = annBar();
      var a = bar && annSticks() ? Math.round(bar.offsetHeight) : 0;
      setVar(html, '--rr-announcement-h', a + 'px');
      /* 6n: the bar slims by sliding up; its layout height never changes */
      setVar(html, '--rr-ann-slim', (a ? Math.max(0, a - (isDesk() ? 34 : 30)) : 0) + 'px');
      var mh = q('#mobile-header');
      var m = mh && mh.offsetHeight ? Math.round(mh.offsetHeight) : 0;
      setVar(html, '--rr-mstack-h', (a + m) + 'px');
      if (mh && m && !isDesk()) {
        /* the backing scales from the header's box to the 52px row; the row
           moves up by half of what it gives up */
        var row = q('.mobile-header__content', mh);
        var rh = row ? Math.round(row.offsetHeight) : 74;
        setVar(mh, '--rr-mhead-k', (52 / m).toFixed(4));
        setVar(mh, '--rr-mrow-shift', Math.max(0, (rh - 52) / 2) + 'px');
      }
    }
    function watchHeights() {
      measureVars();
      if (!win.ResizeObserver) return;
      var ro = new ResizeObserver(function () { raf(measureVars); });
      var bar = annBar();
      var mh = q('#mobile-header');
      if (bar) ro.observe(bar);
      if (mh) ro.observe(mh);
    }

    /* ---- desktop Menu button --------------------------------------------- */
    var MENU_SVG = '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true" focusable="false"><path d="M4 7h16M4 12h16M4 17h16"/></svg>';
    var SEARCH_SVG = '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true" focusable="false"><circle cx="10.5" cy="10.5" r="6.5"/><path d="m20 20-4.5-4.5"/></svg>';

    var navAnimT = null;
    function navAnim() {
      html.classList.add('rr-navanim');
      win.clearTimeout(navAnimT);
      navAnimT = win.setTimeout(function () { html.classList.remove('rr-navanim'); }, 320);
    }

    function buildMenu() {
      var row = q('#header .header__inner-content');
      if (!row || q('.rr-hmenu', row)) return;
      var brand = q('.header__brand', row);
      var b = doc.createElement('button');
      b.type = 'button';
      b.className = 'rr-hmenu';
      b.setAttribute('aria-expanded', 'false');
      b.setAttribute('aria-controls', 'rr-compact-nav');
      b.innerHTML = MENU_SVG + '<span>Menu</span>';
      row.insertBefore(b, brand || row.firstChild);
      var nav = q('#header .sticky-menu-wrapper');
      if (nav && !nav.id) nav.id = 'rr-compact-nav';
      b.addEventListener('click', function (e) {
        e.preventDefault();
        var open = !html.classList.contains('rr-navopen');
        navAnim();
        html.classList.toggle('rr-navopen', open);
        b.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
      doc.addEventListener('click', function (e) {
        if (!html.classList.contains('rr-navopen')) return;
        var t = e.target;
        if (t && t.closest && (t.closest('.rr-hmenu') || t.closest('.sticky-menu-wrapper'))) return;
        navAnim();
        html.classList.remove('rr-navopen');
        b.setAttribute('aria-expanded', 'false');
      });
      doc.addEventListener('keydown', function (e) {
        if ((e.key === 'Escape' || e.keyCode === 27) && html.classList.contains('rr-navopen')) {
          navAnim();
        html.classList.remove('rr-navopen');
          b.setAttribute('aria-expanded', 'false');
          try { b.focus(); } catch (err) { /* focus is a nicety */ }
        }
      });
    }

    /* ---- phone search icon ------------------------------------------------ */
    function buildSearchIcon() {
      var host = q('#mobile-header .mobile-dropdown');
      if (!host || q('.rr-msico', host)) return;
      var b = doc.createElement('button');
      b.type = 'button';
      b.className = 'rr-msico rr-mib';
      b.setAttribute('aria-label', 'Search');
      b.setAttribute('data-rr-msearch-open', '');
      b.innerHTML = SEARCH_SVG;
      /* After the drawer, never between it and the toggle: the theme opens the
         drawer with ".mobile-menu__toggle-icon.is-active + .mobile-menu", and
         an icon in between kept the phone menu shut (28 Sept). The drawer is
         absolutely placed, so the icon still sits right after the burger. */
      var menu = q('.mobile-menu', host);
      var toggle = q('.mobile-menu__toggle-icon', host);
      var after = menu || toggle;
      if (after && after.nextSibling) host.insertBefore(b, after.nextSibling);
      else host.appendChild(b);
    }

    function build() {
      buildMenu();
      buildSearchIcon();
    }

    function boot() {
      build();
      watchHeights();
      holdRest();
      holdAnnRest();
      holdPhoneRest();
      update();
      win.addEventListener('scroll', onScroll, { passive: true });
      win.addEventListener('scroll', function () { lastScroll = Date.now(); }, { passive: true });
      queueGhost();
      var rt = null;
      win.addEventListener('resize', function () {
        win.clearTimeout(rt);
        rt = win.setTimeout(function () { holdRest(); holdAnnRest(); holdPhoneRest(); measureVars(); update(); queueGhost(); }, 150);
      }, { passive: true });
      win.addEventListener('load', function () { holdRest(); measureVars(); queueGhost(); });
      doc.addEventListener('shopify:section:load', function () { ghostEl = null; build(); holdRest(); measureVars(); queueGhost(); });
    }

    win.rrCompact = {
      on: function () { return compact; },
      measure: measure,
      thresholds: function () { return { on: dOn, off: dOff }; }
    };

    if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', boot);
    else boot();
  } catch (e) {
    /* the header still sticks the old way */
  }
})();
